import { v4 } from 'uuid';

import {
  type FileSeriesWidgetOptions,
  isFileSeriesWidget,
} from '@neptune/file-series-widget-domain';
import { type HistogramWidgetOptions, isHistogramWidget } from '@neptune/histogram-widget-domain';
import {
  getWidgetSource,
  type Widget,
  type WidgetLayoutSettings,
  type WidgetOptions,
  type WidgetType,
} from '@neptune/shared/widgets-domain';
import { isValueListWidget, type ValueListWidgetOptions } from '@neptune/value-list-widget-domain';

import { WidgetDefinition } from 'domain/widget/core/domain/widget-definition';
import { type ChartWidgetOptions, isChartWidget } from 'domain/widget/support/chart-widget';
import { sectionWidgetOptionsFromApiToDomain } from 'domain/widget/support/section-widget';

export type DynamicSectionWidget = Widget & {
  type: 'dynamicSection';
  options: DynamicSectionWidgetOptions;
};

export type DynamicSectionWidgetOptions = {
  dynamicSelectionRegex?: string;
  folded?: boolean;
  parentSectionId?: string;
  layoutSettings?: WidgetLayoutSettings;
  chartOptions?: ChartWidgetOptions;
  childWidgetOverrides?: DynamicSectionChildWidgetOverride[];
};

export type DynamicSectionChildWidgetType = Extract<
  WidgetType,
  'chart' | 'histogram' | 'fileSeries' | 'valueList'
>;

type DynamicSectionBaseChildWidgetOverride = {
  source: string;
  widgetType: DynamicSectionChildWidgetType;
  name?: string;
  options?: WidgetOptions;
};

export type DynamicSectionChartChildWidgetOverride = DynamicSectionBaseChildWidgetOverride & {
  widgetType: 'chart';
  options?: Partial<ChartWidgetOptions>;
};

export type DynamicSectionHistogramChildWidgetOverride = DynamicSectionBaseChildWidgetOverride & {
  widgetType: 'histogram';
  options?: Partial<HistogramWidgetOptions>;
};

export type DynamicSectionFileSeriesChildWidgetOverride = DynamicSectionBaseChildWidgetOverride & {
  widgetType: 'fileSeries';
  options?: Partial<FileSeriesWidgetOptions>;
};

export type DynamicSectionValueListChildWidgetOverride = DynamicSectionBaseChildWidgetOverride & {
  widgetType: 'valueList';
  options?: Partial<ValueListWidgetOptions>;
};

export type DynamicSectionChildWidgetOverride =
  | DynamicSectionChartChildWidgetOverride
  | DynamicSectionHistogramChildWidgetOverride
  | DynamicSectionFileSeriesChildWidgetOverride
  | DynamicSectionValueListChildWidgetOverride;

export const REMOVE_DYNAMIC_SECTION_MODAL_NAME = 'REMOVE_DYNAMIC_SECTION_MODAL';

export function isDynamicSectionWidget(widget: Widget | undefined): widget is DynamicSectionWidget {
  return widget?.type === 'dynamicSection';
}

export function createDynamicSectionWidget({
  name,
  options = {},
}: {
  name?: string;
  options?: DynamicSectionWidgetOptions;
}): DynamicSectionWidget {
  return {
    id: v4(),
    type: 'dynamicSection',
    name,
    sources: [],
    options,
  };
}

export function updateWidgetInDynamicSection(
  section: DynamicSectionWidget,
  childWidget: Widget,
): DynamicSectionWidget {
  const childWidgetOverride = normalizeChildWidgetOverride(
    createDynamicSectionChildWidgetOverride(childWidget),
  );

  if (!childWidgetOverride) {
    return removeChildWidgetOverride(section, childWidget);
  }

  return {
    ...section,
    options: {
      ...section.options,
      childWidgetOverrides: [
        childWidgetOverride,
        ...(section.options.childWidgetOverrides ?? []).filter(
          (override) => !isChildWidgetIdentityEqual(override, childWidgetOverride),
        ),
      ],
    },
  };
}

export function createDynamicSectionChildWidgetOverride(
  widget: Widget,
): DynamicSectionChildWidgetOverride | undefined {
  const source = getWidgetSource(widget);
  const name = widget.name !== undefined ? { name: widget.name } : {};

  if (!source) {
    return undefined;
  }

  if (isChartWidget(widget)) {
    return {
      source,
      widgetType: 'chart',
      ...name,
      ...(widget.options !== undefined ? { options: widget.options } : {}),
    };
  }

  if (isHistogramWidget(widget)) {
    return {
      source,
      widgetType: 'histogram',
      ...name,
      ...(widget.options !== undefined ? { options: widget.options } : {}),
    };
  }

  if (isFileSeriesWidget(widget)) {
    return {
      source,
      widgetType: 'fileSeries',
      ...name,
      ...(widget.options !== undefined ? { options: widget.options } : {}),
    };
  }

  if (isValueListWidget(widget)) {
    return {
      source,
      widgetType: 'valueList',
      ...name,
      ...(widget.options !== undefined ? { options: widget.options } : {}),
    };
  }

  return undefined;
}

type DynamicSectionChildWidgetIdentity = Pick<
  DynamicSectionBaseChildWidgetOverride,
  'source' | 'widgetType'
>;

function getDynamicSectionChildWidgetIdentity(
  widget: Widget,
): DynamicSectionChildWidgetIdentity | undefined {
  const source = getWidgetSource(widget);

  if (!source || !isDynamicSectionChildWidgetType(widget.type)) {
    return undefined;
  }

  return {
    source,
    widgetType: widget.type,
  };
}

function isDynamicSectionChildWidgetType(
  widgetType: WidgetType,
): widgetType is DynamicSectionChildWidgetType {
  return (
    widgetType === 'chart' ||
    widgetType === 'histogram' ||
    widgetType === 'fileSeries' ||
    widgetType === 'valueList'
  );
}

function isChildWidgetIdentityEqual(
  left: DynamicSectionChildWidgetIdentity,
  right: DynamicSectionChildWidgetIdentity,
): boolean {
  return left.source === right.source && left.widgetType === right.widgetType;
}

function normalizeChildWidgetOverride(
  childWidgetOverride: DynamicSectionChildWidgetOverride | undefined,
): DynamicSectionChildWidgetOverride | undefined {
  if (!childWidgetOverride) {
    return undefined;
  }

  const hasNameOverride = childWidgetOverride.name !== undefined && childWidgetOverride.name !== '';
  const hasOptionsOverride = !isEmptyWidgetOptions(childWidgetOverride.options);
  const hasPersistableOverride = hasNameOverride || hasOptionsOverride;

  if (!hasPersistableOverride) {
    return undefined;
  }

  if (!hasNameOverride) {
    const { name: _name, ...overrideWithoutName } = childWidgetOverride;

    return overrideWithoutName;
  }

  if (!hasOptionsOverride) {
    const { options: _options, ...overrideWithoutOptions } = childWidgetOverride;

    return overrideWithoutOptions;
  }

  return childWidgetOverride;
}

function isEmptyWidgetOptions(options: WidgetOptions | undefined): boolean {
  return options === undefined || Object.values(options).every((value) => value === undefined);
}

function removeChildWidgetOverride(
  section: DynamicSectionWidget,
  widget: Widget,
): DynamicSectionWidget {
  const childWidgetIdentity = getDynamicSectionChildWidgetIdentity(widget);
  const childWidgetOverrides = section.options.childWidgetOverrides;

  if (!childWidgetIdentity || !childWidgetOverrides) {
    return section;
  }

  const filteredChildWidgetOverrides = childWidgetOverrides.filter(
    (override) => !isChildWidgetIdentityEqual(override, childWidgetIdentity),
  );

  if (filteredChildWidgetOverrides.length === childWidgetOverrides.length) {
    return section;
  }

  const { childWidgetOverrides: _childWidgetOverrides, ...optionsWithoutChildWidgetOverrides } =
    section.options;
  const updatedOptions =
    filteredChildWidgetOverrides.length > 0
      ? {
          ...section.options,
          childWidgetOverrides: filteredChildWidgetOverrides,
        }
      : optionsWithoutChildWidgetOverrides;

  return {
    ...section,
    options: updatedOptions,
  };
}

export function applyDynamicSectionChildWidgetOverride(
  widget: Widget,
  childWidgetOverrides: DynamicSectionChildWidgetOverride[] | undefined,
): Widget {
  const childWidgetIdentity = getDynamicSectionChildWidgetIdentity(widget);

  if (!childWidgetIdentity) {
    return widget;
  }

  const childWidgetOverride = childWidgetOverrides?.find((override) =>
    isChildWidgetIdentityEqual(override, childWidgetIdentity),
  );

  if (!childWidgetOverride) {
    return widget;
  }

  return {
    ...widget,
    ...(childWidgetOverride.name !== undefined ? { name: childWidgetOverride.name } : {}),
    ...(childWidgetOverride.options !== undefined
      ? { options: { ...(widget.options ?? {}), ...childWidgetOverride.options } }
      : {}),
  };
}

function dynamicSectionWidgetOptionsFromApiToDomain(
  options?: Record<string, any>,
): DynamicSectionWidgetOptions | undefined {
  return {
    dynamicSelectionRegex: options?.dynamicSelectionRegex,
    ...sectionWidgetOptionsFromApiToDomain(options),
    chartOptions: options?.chartOptions,
    childWidgetOverrides: options?.childWidgetOverrides,
  };
}

export const dynamicSectionWidgetDefinition: WidgetDefinition = {
  type: 'dynamicSection',
  fromApiToDomain: dynamicSectionWidgetOptionsFromApiToDomain,
};
